import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Bell,
    AlertTriangle,
    XCircle,
    Info,
    CheckCircle,
    Search,
    Filter,
    MoreHorizontal,
    Eye,
    Check,
    CheckCheck,
    Archive,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Clock,
    TrendingUp,
    Calendar,
    ExternalLink,
    X,
    RefreshCw,
    Loader2
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';
import { useAlerts, useAlertStats, useLoans } from '../hooks';

function Alerts() {
    const { t } = useTranslation();
    const { addLog } = useActivityLog();


    const ALERT_CONFIG = {
        critical: {
            icon: XCircle,
            label: t('alerts.critical'),
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            textColor: 'text-red-700',
            iconColor: 'text-red-500',
            badgeColor: 'bg-red-100 text-red-700',
        },
        warning: {
            icon: AlertTriangle,
            label: t('common.warning'),
            bgColor: 'bg-amber-50',
            borderColor: 'border-amber-200',
            textColor: 'text-amber-700',
            iconColor: 'text-amber-500',
            badgeColor: 'bg-amber-100 text-amber-700',
        },
        info: {
            icon: Info,
            label: t('common.info'),
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            textColor: 'text-blue-700',
            iconColor: 'text-blue-500',
            badgeColor: 'bg-blue-100 text-blue-700',
        },
    };


    const CATEGORY_CONFIG = {
        breach: { label: t('alerts.breach'), icon: XCircle },
        approaching: { label: t('alerts.approaching'), icon: TrendingUp },
        reporting: { label: t('alerts.reporting'), icon: Calendar },
        maturity: { label: t('alerts.maturity'), icon: Clock },
        compliance: { label: t('alerts.compliance'), icon: CheckCircle },
    };


    const [showArchived, setShowArchived] = useState(false);
    const { alerts: dbAlerts, loading, error, refetch, markAsRead: dbMarkAsRead, markAllAsRead: dbMarkAllAsRead, archiveAlert: dbArchiveAlert } = useAlerts({ isArchived: showArchived });
    const { stats: dbStats, loading: statsLoading } = useAlertStats();
    const { loans: dbLoans } = useLoans();


    const alertsData = useMemo(() => {
        return dbAlerts.map(alert => ({
            id: alert.id,
            loanId: alert.loan_id,
            type: alert.type,
            category: alert.category,
            title: alert.title,
            message: alert.message,
            covenant: alert.covenant_name,
            currentValue: alert.current_value,
            threshold: alert.threshold,
            isRead: Boolean(alert.is_read),
            isArchived: Boolean(alert.is_archived),
            createdAt: alert.created_at
        }));
    }, [dbAlerts]);


    const loansMap = useMemo(() => {
        const map = {};
        dbLoans.forEach(loan => {
            map[loan.id] = loan.borrower;
        });
        return map;
    }, [dbLoans]);


    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        type: 'all',
        status: 'all',
        category: 'all',
    });
    const [selectedAlerts, setSelectedAlerts] = useState([]);
    const [activeMenu, setActiveMenu] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;


    const stats = useMemo(() => {
        if (dbStats) {
            return {
                total: dbStats.total || 0,
                unread: dbStats.unread || 0,
                critical: dbStats.critical || 0,
                warning: dbStats.warning || 0,
                info: dbStats.info || 0,
            };
        }
        return { total: 0, unread: 0, critical: 0, warning: 0, info: 0 };
    }, [dbStats]);


    const filteredAlerts = useMemo(() => {
        let result = [...alertsData];


        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(a =>
                a.title.toLowerCase().includes(query) ||
                a.message.toLowerCase().includes(query)
            );
        }


        if (filters.type !== 'all') {
            result = result.filter(a => a.type === filters.type);
        }


        if (filters.status !== 'all') {
            result = result.filter(a =>
                filters.status === 'unread' ? !a.isRead : a.isRead
            );
        }


        if (filters.category !== 'all') {
            result = result.filter(a => a.category === filters.category);
        }


        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return result;
    }, [alertsData, searchQuery, filters]);


    const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
    const paginatedAlerts = filteredAlerts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );


    const markAsRead = async (alertId) => {
        const alert = alertsData.find(a => a.id === alertId);
        await dbMarkAsRead(alertId);
        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.ALERT,
            action: 'mark_alert_read',
            message: `Alerte marquée comme lue: ${alert?.title || alertId}`,
            details: { alertId, alertType: alert?.type, alertTitle: alert?.title }
        });
    };

    const markAsUnread = (alertId) => {

        console.log('Mark as unread not implemented in database');
    };

    const archiveAlert = async (alertId) => {
        const alert = alertsData.find(a => a.id === alertId);
        await dbArchiveAlert(alertId);
        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.ALERT,
            action: 'archive_alert',
            message: `Alerte archivée: ${alert?.title || alertId}`,
            details: { alertId, alertType: alert?.type, alertTitle: alert?.title }
        });
        setActiveMenu(null);
    };

    const deleteAlert = (alertId) => {

        console.log('Delete not implemented in database');
        setActiveMenu(null);
    };

    const markAllAsRead = async () => {
        await dbMarkAllAsRead();
        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.ALERT,
            action: 'mark_all_alerts_read',
            message: `Toutes les alertes marquées comme lues (${stats.unread} alertes)`,
            details: { count: stats.unread }
        });
    };

    const markSelectedAsRead = async () => {
        for (const id of selectedAlerts) {
            await dbMarkAsRead(id);
        }
        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.ALERT,
            action: 'mark_selected_alerts_read',
            message: `${selectedAlerts.length} alertes marquées comme lues`,
            details: { count: selectedAlerts.length, alertIds: selectedAlerts }
        });
        setSelectedAlerts([]);
    };

    const archiveSelected = async () => {
        for (const id of selectedAlerts) {
            await dbArchiveAlert(id);
        }
        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.ALERT,
            action: 'archive_selected_alerts',
            message: `${selectedAlerts.length} alertes archivées`,
            details: { count: selectedAlerts.length, alertIds: selectedAlerts }
        });
        setSelectedAlerts([]);
    };

    const toggleSelectAll = () => {
        if (selectedAlerts.length === paginatedAlerts.length) {
            setSelectedAlerts([]);
        } else {
            setSelectedAlerts(paginatedAlerts.map(a => a.id));
        }
    };

    const toggleSelect = (alertId) => {
        setSelectedAlerts(prev =>
            prev.includes(alertId)
                ? prev.filter(id => id !== alertId)
                : [...prev, alertId]
        );
    };


    const getLoanName = (loanId) => {
        return loansMap[loanId] || 'Unknown';
    };


    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const activeFiltersCount = Object.values(filters).filter(v => v !== 'all').length;


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800">{t('alerts.title')}</h2>
                    <p className="text-slate-500 text-sm sm:text-base">
                        {stats.unread} {t('alerts.subtitle')}
                    </p>
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <button
                        onClick={markAllAsRead}
                        disabled={stats.unread === 0}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                        <CheckCheck size={18} />
                        <span className="hidden sm:inline">{t('alerts.markAllAsRead')}</span>
                        <span className="sm:hidden">{t('alerts.readAll')}</span>
                    </button>
                    <button
                        onClick={() => setShowArchived(!showArchived)}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base ${
                            showArchived
                                ? 'bg-slate-800 text-white'
                                : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <Archive size={18} />
                        <span className="hidden sm:inline">{showArchived ? t('alerts.showActive') : t('alerts.viewArchived')}</span>
                        <span className="sm:hidden">{showArchived ? t('alerts.active') : t('alerts.archived')}</span>
                    </button>
                </div>
            </div>

            {}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
                            <Bell size={18} className="text-slate-600 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <p className="text-xl sm:text-2xl font-bold text-slate-800">{stats.total}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{t('alerts.totalActive')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                            <Clock size={18} className="text-blue-600 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <p className="text-xl sm:text-2xl font-bold text-slate-800">{stats.unread}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{t('alerts.unread')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
                            <XCircle size={18} className="text-red-600 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.critical}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{t('alerts.critical')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                            <AlertTriangle size={18} className="text-amber-600 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.warning}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{t('alerts.warning')}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 col-span-2 sm:col-span-1">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                            <Info size={18} className="text-emerald-600 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <p className="text-xl sm:text-2xl font-bold text-emerald-600">{stats.info}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{t('common.info')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4">
                <div className="flex gap-2 sm:gap-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder={t('alerts.searchAlerts')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm sm:text-base ${
                            showFilters || activeFiltersCount > 0
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <Filter size={18} />
                        <span className="hidden sm:inline">{t('alerts.filters')}</span>
                        {activeFiltersCount > 0 && (
                            <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
                        )}
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t('alerts.type')}</label>
                            <select
                                value={filters.type}
                                onChange={(e) => setFilters(p => ({ ...p, type: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm sm:text-base"
                            >
                                <option value="all">{t('alerts.allTypes')}</option>
                                <option value="critical">{t('alerts.critical')}</option>
                                <option value="warning">{t('common.warning')}</option>
                                <option value="info">{t('common.info')}</option>
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t('alerts.status')}</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm sm:text-base"
                            >
                                <option value="all">{t('alerts.allStatus')}</option>
                                <option value="unread">{t('alerts.unread')}</option>
                                <option value="read">{t('alerts.read')}</option>
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t('alerts.category')}</label>
                            <select
                                value={filters.category}
                                onChange={(e) => setFilters(p => ({ ...p, category: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm sm:text-base"
                            >
                                <option value="all">{t('alerts.allCategories')}</option>
                                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                                    <option key={key} value={key}>{config.label}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={() => setFilters({ type: 'all', status: 'all', category: 'all' })}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800"
                        >
                            <X size={18} />
                            {t('alerts.reset')}
                        </button>
                    </div>
                )}
            </div>

            {}
            {selectedAlerts.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <span className="text-blue-700 font-medium">
            {selectedAlerts.length} {t(selectedAlerts.length > 1 ? 'alerts.selectedPlural' : 'alerts.selected')}
          </span>
                    <div className="flex gap-2">
                        <button
                            onClick={markSelectedAsRead}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-100 text-sm"
                        >
                            <Check size={16} />
                            {t('alerts.markAsRead')}
                        </button>
                        <button
                            onClick={archiveSelected}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-100 text-sm"
                        >
                            <Archive size={16} />
                            {t('alerts.archive')}
                        </button>
                        <button
                            onClick={() => setSelectedAlerts([])}
                            className="p-1.5 hover:bg-blue-100 rounded-lg"
                        >
                            <X size={18} className="text-blue-600" />
                        </button>
                    </div>
                </div>
            )}

            {}
            {activeMenu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setActiveMenu(null)}
                    />
                    <div
                        className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
                        style={(() => {
                            const btn = document.querySelector(`[data-alert-id="${activeMenu}"]`);
                            if (!btn) return { top: 0, right: 40 };

                            const rect = btn.getBoundingClientRect();
                            const dropdownHeight = 200;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const spaceAbove = rect.top;


                            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                                return {
                                    bottom: window.innerHeight - rect.top + 4,
                                    right: window.innerWidth - rect.right,
                                };
                            }

                            return {
                                top: rect.bottom + 4,
                                right: window.innerWidth - rect.right,
                            };
                        })()}
                    >
                        {(() => {
                            const alert = alertsData.find(a => a.id === activeMenu);
                            if (!alert) return null;
                            return (
                                <>
                                    <Link
                                        to={`/loans/${alert.loanId}`}
                                        onClick={() => setActiveMenu(null)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                        <Eye size={16} />
                                        {t('alerts.viewLoan')}
                                    </Link>
                                    {alert.isRead ? (
                                        <button
                                            onClick={() => { markAsUnread(alert.id); setActiveMenu(null); }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                        >
                                            <RefreshCw size={16} />
                                            {t('alerts.markAsUnread')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => { markAsRead(alert.id); setActiveMenu(null); }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                        >
                                            <Check size={16} />
                                            {t('alerts.markAsRead')}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => archiveAlert(alert.id)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                        <Archive size={16} />
                                        {t('alerts.archive')}
                                    </button>
                                    <hr className="my-1 border-slate-100" />
                                    <button
                                        onClick={() => deleteAlert(alert.id)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                        <Trash2 size={16} />
                                        {t('common.delete')}
                                    </button>
                                </>
                            );
                        })()}
                    </div>
                </>
            )}

            {}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {}
                <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 bg-slate-50 border-b border-slate-200">
                    <input
                        type="checkbox"
                        checked={selectedAlerts.length === paginatedAlerts.length && paginatedAlerts.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm font-medium text-slate-600 flex-1">{t('alerts.alert')}</span>
                    <span className="hidden md:block text-sm font-medium text-slate-600 w-32">{t('alerts.loan')}</span>
                    <span className="hidden sm:block text-sm font-medium text-slate-600 w-24">{t('alerts.time')}</span>
                    <span className="text-sm font-medium text-slate-600 w-10 sm:w-20">{t('alerts.actions')}</span>
                </div>

                {}
                <div className="divide-y divide-slate-100">
                    {paginatedAlerts.length > 0 ? (
                        paginatedAlerts.map((alert) => {
                            const config = ALERT_CONFIG[alert.type];
                            const Icon = config.icon;
                            const isSelected = selectedAlerts.includes(alert.id);

                            return (
                                <div
                                    key={alert.id}
                                    className={`flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors ${
                                        !alert.isRead ? 'bg-blue-50/30' : ''
                                    }`}
                                >
                                    {}
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSelect(alert.id)}
                                        className="w-4 h-4 rounded border-slate-300"
                                    />

                                    {}
                                    <div className="flex-1 flex items-start gap-2 sm:gap-3 min-w-0">
                                        <div className={`p-1.5 sm:p-2 rounded-lg ${config.bgColor} shrink-0`}>
                                            <Icon size={16} className={`${config.iconColor} sm:w-[18px] sm:h-[18px]`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                                <h4 className={`font-medium text-sm sm:text-base truncate ${!alert.isRead ? 'text-slate-900' : 'text-slate-700'}`}>
                                                    {alert.title}
                                                </h4>
                                                {!alert.isRead && (
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />
                                                )}
                                                <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full shrink-0 ${config.badgeColor}`}>
                          {config.label}
                        </span>
                                            </div>
                                            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">{alert.message}</p>
                                            {}
                                            <div className="flex items-center gap-2 mt-1 sm:hidden">
                                                <span className="text-xs text-slate-400">{formatRelativeTime(alert.createdAt)}</span>
                                                <span className="text-slate-300">•</span>
                                                <Link
                                                    to={`/loans/${alert.loanId}`}
                                                    className="text-xs text-blue-600 hover:underline"
                                                >
                                                    {getLoanName(alert.loanId)}
                                                </Link>
                                            </div>
                                            {alert.covenant && (
                                                <div className="hidden sm:flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-400">
                            {alert.covenant}: {alert.currentValue} vs {alert.threshold}
                          </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {}
                                    <div className="hidden md:block w-32">
                                        <Link
                                            to={`/loans/${alert.loanId}`}
                                            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                        >
                                            {getLoanName(alert.loanId)}
                                            <ExternalLink size={12} />
                                        </Link>
                                    </div>

                                    {}
                                    <div className="hidden sm:block w-24 text-sm text-slate-500">
                                        {formatRelativeTime(alert.createdAt)}
                                    </div>

                                    {}
                                    <div className="w-10 sm:w-20 flex justify-end">
                                        <button
                                            data-alert-id={alert.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveMenu(activeMenu === alert.id ? null : alert.id);
                                            }}
                                            className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg"
                                        >
                                            <MoreHorizontal size={18} className="text-slate-500" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 sm:py-12 px-4">
                            <Bell size={40} className="mx-auto text-slate-300 mb-3 sm:mb-4 sm:w-12 sm:h-12" />
                            <h3 className="text-base sm:text-lg font-medium text-slate-800 mb-1">
                                {showArchived ? t('alerts.noArchivedAlerts') : t('alerts.noAlertsFound')}
                            </h3>
                            <p className="text-sm sm:text-base text-slate-500">
                                {searchQuery || activeFiltersCount > 0
                                    ? t('alerts.tryAdjustingFilters')
                                    : showArchived
                                        ? t('alerts.archivedAlertsWillAppearHere')
                                        : t('alerts.allCaughtUp')
                                }
                            </p>
                        </div>
                    )}
                </div>

                {}
                {totalPages > 1 && (
                    <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-xs sm:text-sm text-slate-500 order-2 sm:order-1">
                            {t('alerts.showing')} {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredAlerts.length)} {t('alerts.of')} {filteredAlerts.length}
                        </p>
                        <div className="flex items-center gap-1 order-1 sm:order-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={18} />
                            </button>

                            {}
                            {(() => {
                                const pages = [];
                                const showEllipsisStart = currentPage > 3;
                                const showEllipsisEnd = currentPage < totalPages - 2;


                                pages.push(1);

                                if (showEllipsisStart) {
                                    pages.push('ellipsis-start');
                                }


                                for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                                    if (!pages.includes(i)) {
                                        pages.push(i);
                                    }
                                }

                                if (showEllipsisEnd) {
                                    pages.push('ellipsis-end');
                                }


                                if (totalPages > 1 && !pages.includes(totalPages)) {
                                    pages.push(totalPages);
                                }

                                return pages.map((page, idx) => {
                                    if (typeof page === 'string') {
                                        return (
                                            <span key={page} className="px-2 text-slate-400">...</span>
                                        );
                                    }
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-10 h-10 rounded-lg font-medium text-sm ${
                                                currentPage === page
                                                    ? 'bg-blue-600 text-white'
                                                    : 'hover:bg-slate-100 text-slate-700'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    );
                                });
                            })()}

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Alerts;